// Top level libraries and 3rd party dependencies
import React, { Component } from 'react';
import { object, func, string } from 'prop-types';
import { equals, contains, isEmpty } from 'ramda';

import {SAVE_PIN_COPY, DEFAULT_HIPAA_SAFE_LABEL} from '../../constants/AppConstants';
import LoadingState from '../LoadingState';
import ErrorMessage from '../ErrorMessage';
import * as SessionTypes from '../../constants/ActionTypes';
import Footer from '../Footer';

// Page styles
import cx from 'classnames';
import s from './SavePIN.css';


export default class SavePIN extends Component {
    static propTypes = {
        conversationHash: string,
        conversationDetails: object,
        error: object,
        loadingState: object,
        savePIN: func.isRequired,
    };

    constructor(props) {
        super(props);

        this.PIN_LENGTH = 5;

        this.state = {
            PIN: '',
            confirmPIN: '',
            mismatchedPIN: false,
        };

        // Refs are declared in the render, so inputs is not a state variable to avoid a loop
        this.inputs = {};
    }

    // enforce 5 as maxLength with slice()
    handleChangePIN = event => {
        if (!this.mismatchedPIN()) {
            this.setState({ mismatchedPIN: false });
        }
        this.setState({[event.target.name]: event.target.value.slice(0, 5)});
    };

    /**
     * input type=number allows for floating point numbers! 4e+3E is valid. We use this to prevent + = . e and number pad .
     * http://keycode.info/
     */
    handleFloatingPointKeys = event => {
        let keyCode =  event.keyCode;

        const isNotWanted = (keyCode === 69 || keyCode === 101 || keyCode === 187 || keyCode === 189 ||keyCode === 190 || keyCode === 110 || keyCode === 107 || keyCode === 109);

        if (isNotWanted) event.preventDefault();
    };

    // Auto-tab inputs at maxlength, unless it's backspace, delete, shift, tab, or an arrow key
    focusInput = (event, id) => {
        const allowedMaxInputKeys = [8, 9, 16, 37, 38, 39, 40, 46];

        if (event.target.value.length >= event.target.maxLength && allowedMaxInputKeys.indexOf(event.keyCode) === -1) {
            this.inputs[id].focus();
        }
    };

    // Tests will fail if we do not give this Ramda method what it expects, an array. this.props.loadingState.requests returns an array.
    isLoadingSavePIN = loadingState => contains(SessionTypes.SAVE_PIN_REQUEST, loadingState.requests || []);

    handleSubmitPIN = e => {
        e.preventDefault();

        const { conversationHash, savePIN } = this.props;
        const { confirmPIN } = this.state;

        if (this.mismatchedPIN()) {
            this.setState({ mismatchedPIN: true });
            return false;
        }

        this.setState({ mismatchedPIN: false });

        if (this.isValidPIN()) {
            // We pass along the conversationHash in order to display Login
            const request = {
                conversationHash,
                PIN: confirmPIN,
            };

            savePIN(request);
            e.target.blur();
        }
    };

    // Check for both state values AND inequality to avoid displaying the error if one field is empty.
    mismatchedPIN = () => {
        const { PIN, confirmPIN } = this.state;
        return PIN && confirmPIN && !equals(PIN, confirmPIN);
    };

    handlePINBlur = () => !this.mismatchedPIN() ? this.setState({ mismatchedPIN: false }) : this.setState({ mismatchedPIN: true });

    isValidPIN = () => {
        const { PIN, confirmPIN } = this.state;

        return equals(PIN, confirmPIN) && PIN.length === this.PIN_LENGTH && confirmPIN.length === this.PIN_LENGTH;
    };

    render() {
        const { error, loadingState, conversationDetails } = this.props;
        const { confirmPIN, mismatchedPIN, PIN, } = this.state;

        const isLoadingSavePIN = this.isLoadingSavePIN(loadingState);
        const isValidPIN = this.isValidPIN();

        const hipaaSafePracticeName = `${conversationDetails.practiceName}` === DEFAULT_HIPAA_SAFE_LABEL
            ? `your ` + DEFAULT_HIPAA_SAFE_LABEL : `${conversationDetails.practiceName}`;

        return (
            <form
                className={cx(s.component, error ? 'hasErrors' : '')}
                id="savePatientPIN"
                onSubmit={this.handleSubmitPIN}
            >
                <div id="conversationDetails" className={cx(s.alignItems, s.componentBlock, s.verticalSpacing, s.heading)}>
                    <h3>{conversationDetails.practiceName}</h3>
                    <label>{conversationDetails.practicePhone}</label>
                </div>

                <hr className={s.divider} />

                <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing, s.heading)}>
                    <p>{SAVE_PIN_COPY}</p>
                </div>

                <div className={cx('formGroup', s.verticalSpacing)}>
                    <div className={cx(s.componentBlock, 'fullWidth')}>
                        <input
                            id="PIN"
                            name="PIN"
                            type="number"
                            pattern="[0-9]*"
                            className={cx('inputControl', s.bigInput, s.fauxPassword)}
                            maxLength="5"
                            onChange={this.handleChangePIN}
                            onBlur={this.handlePINBlur}
                            onKeyDown={this.handleFloatingPointKeys}
                            onKeyUp={(e) => this.focusInput(e, 'confirmPIN')}
                            value={PIN}
                            required
                        />
                        <label
                            htmlFor="PIN"
                            className={s.heading}
                        >
                            <span>Enter PIN</span>
                        </label>
                    </div>
                </div>

                <div className={cx('formGroup', s.verticalSpacing)}>
                    <div className={cx(s.componentBlock, 'fullWidth')}>
                        <input
                            id="confirmPIN"
                            name="confirmPIN"
                            type="number"
                            pattern="[0-9]*"
                            ref={input => { this.inputs['confirmPIN'] = input; }}
                            className={cx('inputControl', s.bigInput, s.fauxPassword)}
                            maxLength="5"
                            onChange={this.handleChangePIN}
                            onBlur={this.handlePINBlur}
                            onKeyDown={this.handleFloatingPointKeys}
                            onKeyUp={(e) => this.focusInput(e, 'submit')}
                            value={confirmPIN}
                            required
                        />
                        <label
                            htmlFor="confirmPIN"
                            className={s.heading}
                        >
                            <span>Confirm PIN</span>
                        </label>
                    </div>
                </div>

                {mismatchedPIN &&
                    <p>PINs do not match. Please try again</p>
                }

                {error && !isEmpty(conversationDetails) &&
                    <ErrorMessage
                        title="Failure to authenticate"
                        message={`${error.message}. If you have any questions or concerns, please contact ${hipaaSafePracticeName}.`}
                    />
                }

                <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing)}>
                    <button
                        className={cx(s.btn, s.primary)}
                        id="submitSavePatientPIN"
                        type="submit"
                        ref={input => { this.inputs['submit'] = input; }}
                        disabled={!isValidPIN || isLoadingSavePIN}
                    >
                        {isLoadingSavePIN ?
                            <LoadingState context="savePINForm"/> :
                            <span className={s.btnText}>Save PIN</span>
                        }
                    </button>
                </div>

                <Footer />
            </form>
        );
    }
}